<script lang="ts">
  import createFrameNode from "@shared/common/node/create/frame";
  import createTextNode from "@shared/common/node/create/text";
  import type { ICardWrapperNode } from "@shared/common/node/type";
  import type { ICampaignListDataItem } from "@shared/common/types/data-source";
  import { getStore } from "../../context";
  import Frame from "./frame.svelte";
  import Text from "./text.svelte";
  const { device: deviceNullable } = getStore();

  export let idx: number;
  export let card: ICardWrapperNode["card"];
  export let content: ICampaignListDataItem["content"];
  let device: "MOBILE" | "DESKTOP" = $deviceNullable ?? "MOBILE";
  let showDescription =
    !!content.description &&
    Boolean(
      new DOMParser().parseFromString(String(content.description), "text/html")
        .body.textContent
    );

  $: {
    device = $deviceNullable ?? "MOBILE";
  }

  $: {
    showDescription =
      !!content.description &&
      Boolean(
        new DOMParser().parseFromString(
          String(content.description),
          "text/html"
        ).body.textContent
      );
  }
</script>

<Frame
  node={createFrameNode({
    maxWidth: card.maxWidth,
    minWidth: card.minWidth,
    padding: card.padding,
    alignHorizontal: "CENTER",
    layoutMode: idx % 2 === 0 ? "HORIZONTAL" : "HORIZONTAL_REVERSE",
    gap: 100,
    primaryAxisAlignItems: "CENTER",
    counterAxisAlignItems: "CENTER",
  })}
  hideShadow={false}
>
  <Frame
    node={createFrameNode({
      width: { value: 46, unit: "%" },
      maxWidth: { value: 850, unit: "px" },
      minWidth: { value: 580, unit: "px" },
      padding: {
        top: { value: 0, unit: "px" },
        bottom: { value: 0, unit: "px" },
        left: { value: 0, unit: "px" },
        right: { value: 0, unit: "px" },
      },
      fills: [
        {
          type: "SOLID",
          visible: false,
          color: { r: 255, g: 255, b: 255 },
          opacity: 0.25,
        },
        {
          type: "IMAGE",
          scaleMode: "FILL",
          path: String(content[`${device.toLowerCase()}ThumbnailUrl`]),
          fileType: "jpg",
          visible: true,
          video: {
            fileName: "",
            originalFileName: "",
            originalFileType: "",
            thumbnailPath: "",
            thumbnailType: "",
          },
          width: {
            value: Number(content[`${device.toLowerCase()}ThumbnailWidth`]),
            unit: "px",
          },
          height: {
            value: Number(content[`${device.toLowerCase()}ThumbnailHeight`]),
            unit: "px",
          },
        },
        {
          type: "SOLID",
          visible: false,
          color: { r: 255, g: 255, b: 255 },
          opacity: 1,
        },
      ],
      alignHorizontal: idx % 2 === 0 ? "LEFT" : "RIGHT",
      aspectRatio: { type: "MANUAL", widthRatio: 1, heightRatio: 1 },
    })}
    hideShadow={false}
  ></Frame>
  <Frame
    node={createFrameNode({
      width: { value: 42, unit: "%" },
      maxWidth: { value: 780, unit: "px" },
      minWidth: { value: 580, unit: "px" },
      padding: {
        top: { value: 0, unit: "px" },
        bottom: { value: 0, unit: "px" },
        left: { value: 0, unit: "px" },
        right: { value: 0, unit: "px" },
      },
      alignHorizontal: idx % 2 === 0 ? "RIGHT" : "LEFT",
      gap: 40,
    })}
    hideShadow={false}
  >
    <Frame
      node={createFrameNode({
        padding: {
          top: { value: 0, unit: "px" },
          bottom: { value: 0, unit: "px" },
          left: { value: 0, unit: "px" },
          right: { value: 0, unit: "px" },
        },
      })}
      hideShadow={false}
    >
      <Text
        node={createTextNode({
          alignHorizontal: "LEFT",
          content: String(content.title),
          textAlignHorizontal: idx % 2 === 0 ? "LEFT" : "RIGHT",
          fontSize: card.titleFontSize ?? { value: 50, unit: "px" },
          fontName: card.titleFontName ?? {
            family: "PretendardVariable-v1",
            style: "800",
          },
          wordBreak: "KEEP_ALL",
          lineHeight: { value: 1.6, unit: "" },
          fills: [
            {
              type: "SOLID",
              visible: true,
              color: card.titleFontColor ?? { r: 0, g: 0, b: 0 },
              opacity: 1,
            },
          ],
          padding: {
            top: { value: 0, unit: "px" },
            bottom: { value: 0, unit: "px" },
            left: { value: 0, unit: "px" },
            right: { value: 0, unit: "px" },
          },
        })}
      ></Text>
      {#if showDescription}
        <Text
          node={createTextNode({
            content: String(content.description),
            textAlignHorizontal: idx % 2 === 0 ? "LEFT" : "RIGHT",
            fontSize: card.descriptionFontSize ?? { value: 14, unit: "px" },
            fontName: card.descriptionFontName ?? {
              family: "PretendardVariable-v1",
              style: "400",
            },
            fills: [
              {
                type: "SOLID",
                visible: true,
                color: card.descriptionFontColor ?? { r: 0, g: 0, b: 0 },
                opacity: 1,
              },
            ],
            padding: {
              top: { value: 0, unit: "px" },
              bottom: { value: 0, unit: "px" },
              left: { value: 0, unit: "px" },
              right: { value: 0, unit: "px" },
            },
          })}
        ></Text>
      {/if}
    </Frame>
    <Text
      node={createTextNode({
        content: "<u>VIEW MORE</u>",
        textAlignHorizontal: idx % 2 === 0 ? "LEFT" : "RIGHT",
        fontSize: card.descriptionFontSize ?? { value: 14, unit: "px" },
        fontName: card.descriptionFontName ?? {
          family: "PretendardVariable-v1",
          style: "400",
        },
        fills: [
          {
            type: "SOLID",
            visible: true,
            color: card.descriptionFontColor ?? { r: 0, g: 0, b: 0 },
            opacity: 1,
          },
        ],
        padding: {
          top: { value: 0, unit: "px" },
          bottom: { value: 0, unit: "px" },
          left: { value: 0, unit: "px" },
          right: { value: 0, unit: "px" },
        },
      })}
    ></Text>
  </Frame>
</Frame>
